import React from 'react';

const TimelineContext = React.createContext();

export function TimelineContextProvider({ children }) {
    const [hoveredFrame, setHoveredFrame] = React.useState(null);
    const [clickedFrame, setClickedFrame] = React.useState(null);
    const [popupPosition, setPopupPosition] = React.useState(null);
    const [hoverTimer, setHoverTimer] = React.useState(null);

    const handleFrameHover = React.useCallback((frameData, event) => {
        // Clear any existing timer
        if (hoverTimer) clearTimeout(hoverTimer);

        // If popup is already showing (hoveredFrame or clickedFrame exists), update immediately
        // Otherwise, set 300ms delay before showing
        if (hoveredFrame || clickedFrame) {
            // Update immediately - no delay
            setHoveredFrame(frameData);
            setPopupPosition({ x: event.clientX, y: event.clientY });
            setHoverTimer(null);
        } else {
            // Set 300ms delay before showing first popup
            const timer = setTimeout(() => {
                setHoveredFrame(frameData);
                setPopupPosition({ x: event.clientX, y: event.clientY });
            }, 300);
            setHoverTimer(timer);
        }
    }, [hoverTimer, hoveredFrame, clickedFrame]);

    const handleFrameLeave = React.useCallback(() => {
        // Clear timer if mouse leaves before 300ms
        if (hoverTimer) clearTimeout(hoverTimer);
        setHoverTimer(null);
        // Don't clear hoveredFrame - let popup stay open
        // Popup will only dismiss on click outside (via handleDismiss)
    }, [hoverTimer]);

    const handleFrameClick = React.useCallback((frameData, event) => {
        event.stopPropagation();
        setClickedFrame(frameData);
        setPopupPosition({ x: event.clientX, y: event.clientY });
        // Clear hover when clicking
        setHoveredFrame(null);
        if (hoverTimer) clearTimeout(hoverTimer);
    }, [hoverTimer]);

    const handleDismiss = React.useCallback(() => {
        setClickedFrame(null);
        setHoveredFrame(null);
    }, []);

    return (
        <TimelineContext.Provider value={{
            hoveredFrame,
            clickedFrame,
            popupPosition,
            handleFrameHover,
            handleFrameLeave,
            handleFrameClick,
            handleDismiss
        }}>
            {children}
        </TimelineContext.Provider>
    );
}

export function useTimeline() {
    const context = React.useContext(TimelineContext);
    if (!context) {
        throw new Error('useTimeline must be used within TimelineContextProvider');
    }
    return context;
}
